import {TFunction} from 'i18next';
import {BADGE_MAP} from '../../../common/constant';
import {
  getContentProps,
  getImageUrl,
  I_ContentMeta,
} from '../../../common/state/apiGet';
import {getClientStore} from '../../../common/store';
import {
  formatFromServerDate,
  getNowFromServer,
  getRemainTextShort,
  getTimeStamp,
  SHORT_DATE_FORMAT,
} from '../../../common/util/date';
import {toCamelCase} from '../../../common/util/string';
import {getLocalRecentItemsV4} from '../../../commonSub/function';
import {I_EpisodeItem} from '../../../commonSub/state/apiGet';
import {
  IEpisodeType,
  IMyPageContentItem,
  IPurchaseEpisodeType,
  IRecentItemV4,
} from '../../../commonSub/type';
import {
  ILikeItemV4,
  ILikeV4,
  IMyPagePurchaseEpisode,
  IPurchaseItemV4,
  IPurchaseV4,
  IRecentV4,
  ISortV4,
} from '../type';

interface I_MyPagePurchaseEpisodeItem {
  id: number;
  ticketType: string;
  purchasedDateTime: string;
  licenseExpiresDateTime: string;
  episode: I_PurchaseEpisode;
}
interface I_PurchaseEpisode
  extends Pick<I_EpisodeItem, 'id'>,
    Partial<
      Pick<
        I_EpisodeItem,
        | 'seoId'
        | 'contentId'
        | 'title'
        | 'asset'
        | 'free'
        | 'serialStartDateTime'
        | 'useStartDateTime'
      >
    > {}

interface I_MyPagePurchaseEpisode {
  content: I_ContentMeta;
  episodePurchased: I_MyPagePurchaseEpisodeItem[];
  remainTicketCount: number;
  responseDateTime?: string;
}

export function getMyPageEpisodes({
  data: {
    content,
    episodePurchased = [],
    remainTicketCount = 0,
    responseDateTime,
  },
  t,
}: {
  data: I_MyPagePurchaseEpisode;
  t: TFunction;
}): IMyPagePurchaseEpisode {
  // 회차 아이디가 있는 것만 필터링하기로 협의
  const filteredList = episodePurchased.filter(
    ({episode}) => episode && typeof episode.id === 'number'
  );
  const {title} = getContentProps(content || {}, ['title']);
  return {
    remainTicketCount,
    contentTitle: title,
    episodes: filteredList.map(item => {
      const episode = item.episode;
      const {timeText, isExpired} = getPurhcaseEpisodeTime({
        item,
        t,
        responseDateTime,
      });
      const type = toCamelCase(item.ticketType) as IPurchaseEpisodeType;
      return {
        id: item.id,
        type,
        episode: {
          id: episode.id,
          contentId: episode.contentId || 0,
          sid: episode.seoId || '',
          title: episode.title || '',
          thumbnail: episode.asset
            ? getImageUrl(episode.asset.thumbnailImage)
            : '',
          isFree: !!episode.free,
          isExpired,
          timeText,
          badge: BADGE_MAP[type],
        },
      };
    }),
  };
}

function getPurhcaseEpisodeTime({
  item,
  t,
  responseDateTime,
}: {
  item: I_MyPagePurchaseEpisodeItem;
  t: TFunction;
  responseDateTime?: string;
}): {timeText: string; isExpired: boolean} {
  const type = toCamelCase(item.ticketType);
  if (type === 'possession' || !item.licenseExpiresDateTime) {
    const startDateTime =
      item.episode.serialStartDateTime ?? item.episode.useStartDateTime;
    return {
      timeText: startDateTime
        ? formatFromServerDate(startDateTime, SHORT_DATE_FORMAT)
        : '',
      isExpired: false,
    };
  }
  const now = getNowFromServer(responseDateTime);
  const licenseDateTime = getTimeStamp(item.licenseExpiresDateTime);
  const isExpired = now > licenseDateTime;
  return {
    timeText: isExpired
      ? t('library_tab_download_expired')
      : getRemainTextShort({endDate: licenseDateTime, now}, t),
    isExpired,
  };
}

interface I_MetaSortV4 {
  sort: string;
  sortOptions: Array<{
    name: string;
    value: string;
    message: string;
  }>;
}
function getSortInfo({sort, sortOptions}: I_MetaSortV4): ISortV4 {
  return {
    currentSort: sort,
    sortList: sortOptions.map(item => ({
      label: item.name,
      sortKey: item.value,
      toastMessage: item.message,
    })),
  };
}

function getEpisodeData(item: I_EpisodeItem) {
  return {
    id: item.id,
    sid: item.seoId,
    title: item.title,
    type: toCamelCase(item.useType) as IEpisodeType,
  };
}
interface I_Meta extends I_MetaSortV4 {
  responseDateTime: string;
}
interface I_TabData<T> {
  data: T;
  meta: I_Meta;
}
interface I_RecentItemV4 {
  id: number;
  userId: string;
  readDateTime: string;
  content: I_ContentMeta;
  episode: I_EpisodeItem;
}
export function getRecentData({
  data = [],
  meta,
}: I_TabData<I_RecentItemV4[]>): IRecentV4 {
  const currentSort = meta.sort;
  const isUpdateSort = currentSort === '-CONTENT_UPDATE';
  const localList: IRecentItemV4[] = getLocalRecentItemsV4();
  const serverList: IRecentItemV4[] = data.map(item => ({
    id: item.id,
    episode: getEpisodeData(item.episode),
    content: getContentCardProps(item.content),
    readDateTime: getTimeStamp(item.readDateTime),
  }));

  // 최근 감상 순이 아닌 경우 로컬 리스트는 정렬 기준이 없기 때문에 가장 마지막에 푸시한다.
  if (isUpdateSort) {
    if (serverList.length > 0) {
      const newList = localList.filter(
        localItem =>
          serverList.findIndex(
            serverItem => serverItem.content.id === localItem.content.id
          ) === -1
      );
      serverList.push(...newList);
    } else {
      serverList.push(...localList);
    }
  } else {
    localList.forEach(localItem => {
      const findServerItem = serverList.find(
        serverItem => serverItem.content.id === localItem.content.id
      );
      if (!findServerItem) {
        serverList.push(localItem);
      } else if (findServerItem.readDateTime < localItem.readDateTime) {
        findServerItem.readDateTime = localItem.readDateTime;
        findServerItem.episode = localItem.episode;
      }
    });
  }
  return {
    ...getSortInfo(meta),
    contents: isUpdateSort
      ? serverList
      : serverList.sort((a, b) => b.readDateTime - a.readDateTime),
  };
}

interface I_PurchasedItemV4 {
  id: number;
  content: I_ContentMeta;
  remainTicketCount: number;
  remainLicenseCount: number;
  hidden: boolean;
}
export function getPurchasedData({
  data,
  meta,
  isEdit,
}: I_TabData<I_PurchasedItemV4[]> & {isEdit?: boolean}): IPurchaseV4 {
  const contents: IPurchaseItemV4[] =
    data
      ?.filter(c => !isEdit || !getStoredContents().find(c2 => c.id === c2.id))
      .map(item => ({
        id: item.id,
        isHidden: !!item.hidden,
        remainTicketCount: item.remainTicketCount,
        remainLicenseCount: item.remainLicenseCount,
        content: getContentCardProps(item.content),
      })) || [];
  return {
    ...getSortInfo(meta),
    contents,
  };
}

function getStoredContents() {
  return getClientStore()?.getState().myPage.editTab.own?.contents || [];
}

function getLikeMeta(sort: string): I_MetaSortV4 {
  return {
    sort,
    sortOptions: [
      {
        name: 'library_tab_favorites_sort_added',
        value: '-CONTENT_SUBSCRIBE',
        message: 'library_tab_favorites_sort_added_toast',
      },
      {
        name: 'library_tab_favorites_sort_update',
        value: '-EPISODE_UPDATE',
        message: 'library_tab_favorites_sort_update_toast',
      },
    ],
  };
}
interface I_LikeMetaV4 {
  sort: string;
}
interface I_LikeV4 {
  size: number;
  updateCount: number;
  subscriptionCount: number;
  list: Array<{
    id: number;
    content: I_ContentMeta;
    notificationEnabled: boolean;
    createdDateTime: string;
  }>;
}
export function getLikeData(data: I_LikeV4, meta: I_LikeMetaV4): ILikeV4 {
  const contents: ILikeItemV4[] =
    data?.list?.map(item => ({
      id: item.id,
      isLike: !!item.notificationEnabled,
      content: getContentCardProps(item.content),
    })) || [];
  return {
    ...getSortInfo(getLikeMeta(meta.sort)),
    contents,
    size: data.size,
    updateCount: data.updateCount,
    likeCount: data.subscriptionCount,
  };
}

function getContentCardProps(content: I_ContentMeta): IMyPageContentItem {
  return getContentProps(content, [
    'id',
    'sid',
    'title',
    'thumbImg',
    'mainImg',
    'titleImg',
    'badges',
    'bgImg',
    'isAdult',
    'bgColor',
    'bgColorRgb',
    'genre',
  ]);
}
